import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Loader2,
  HardHat,
  UtensilsCrossed,
  Building2,
  Home,
  FileText,
  Star,
  ChevronRight,
  Plus,
  Sparkles,
  LayoutTemplate,
  Move,
  Pin,
  PanelsTopLeft,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useProposalTemplates } from "../../hooks/useProposalTemplates";
import type {
  ProposalTemplate,
  ClientType,
  BuilderMode,
  FixedPagePolicy,
} from "../../types";

const CLIENT_TYPE_CONFIG: Record<
  ClientType,
  {
    label: string;
    icon: React.ReactNode;
    color: string;
    selectedBg: string;
    description: string;
  }
> = {
  construction: {
    label: "Construction",
    icon: <HardHat className="h-5 w-5" />,
    color: "text-amber-400",
    selectedBg: "border-amber-500/40 bg-amber-500/10",
    description: "Post-construction, progress cleans, final inspections",
  },
  restaurant: {
    label: "Restaurant",
    icon: <UtensilsCrossed className="h-5 w-5" />,
    color: "text-blue-400",
    selectedBg: "border-blue-500/40 bg-blue-500/10",
    description: "FOH, BOH, kitchen equipment, daily and weekly service",
  },
  commercial: {
    label: "Commercial",
    icon: <Building2 className="h-5 w-5" />,
    color: "text-purple-400",
    selectedBg: "border-purple-500/40 bg-purple-500/10",
    description: "Office buildings, retail spaces, common areas",
  },
  residential: {
    label: "Residential",
    icon: <Home className="h-5 w-5" />,
    color: "text-emerald-400",
    selectedBg: "border-emerald-500/40 bg-emerald-500/10",
    description: "Homes, apartments, move-in/move-out cleans",
  },
  other: {
    label: "Other",
    icon: <FileText className="h-5 w-5" />,
    color: "text-white/40",
    selectedBg: "border-white/30 bg-white/5",
    description: "Custom client type",
  },
};

const BUILDER_MODE_CONFIG: Record<
  BuilderMode,
  {
    label: string;
    icon: React.ReactNode;
    color: string;
    description: string;
  }
> = {
  prebuilt: {
    label: "Pre-built",
    icon: <LayoutTemplate className="h-3.5 w-3.5" />,
    color: "text-cyan-300 bg-cyan-500/10 border-cyan-500/20",
    description: "Template-first starting point",
  },
  drag_drop: {
    label: "Drag & drop",
    icon: <Move className="h-3.5 w-3.5" />,
    color: "text-fuchsia-300 bg-fuchsia-500/10 border-fuchsia-500/20",
    description: "Builder-ready starting point",
  },
};

const FIXED_PAGE_POLICY_CONFIG: Record<
  FixedPagePolicy,
  {
    label: string;
    icon: React.ReactNode;
    color: string;
    description: string;
  }
> = {
  template_defined: {
    label: "Template pages",
    icon: <PanelsTopLeft className="h-3.5 w-3.5" />,
    color: "text-white/45 bg-white/5 border-white/10",
    description: "Template controls most page choices",
  },
  brand_defined: {
    label: "Brand-defined fixed pages",
    icon: <Pin className="h-3.5 w-3.5" />,
    color: "text-amber-300 bg-amber-500/10 border-amber-500/20",
    description: "Leans on Brand Setup fixed pages",
  },
  mixed: {
    label: "Mixed fixed pages",
    icon: <Pin className="h-3.5 w-3.5" />,
    color: "text-emerald-300 bg-emerald-500/10 border-emerald-500/20",
    description: "Uses both fixed and template pages",
  },
};

interface Props {
  onSelect: (template: ProposalTemplate) => void;
  selectedTemplateId?: string;
}

function getTemplateClientType(template: ProposalTemplate): ClientType {
  const raw =
    template.client_type ??
    (template.category as ClientType | null | undefined) ??
    "other";

  if (
    raw === "construction" ||
    raw === "restaurant" ||
    raw === "commercial" ||
    raw === "residential" ||
    raw === "other"
  ) {
    return raw;
  }

  return "other";
}

function getTemplateServiceCount(template: ProposalTemplate): number {
  if (
    Array.isArray(template.template_services) &&
    template.template_services.length > 0
  ) {
    return template.template_services.length;
  }

  if (Array.isArray(template.default_line_items)) {
    return template.default_line_items.length;
  }

  return 0;
}

function getTemplatePageCount(template: ProposalTemplate): number {
  if (
    Array.isArray(template.template_static_pages) &&
    template.template_static_pages.length > 0
  ) {
    return template.template_static_pages.length;
  }

  if (Array.isArray(template.default_static_page_ids)) {
    return Array.from(
      new Set(
        template.default_static_page_ids.filter(
          (pageId): pageId is string =>
            typeof pageId === "string" && pageId.trim().length > 0
        )
      )
    ).length;
  }

  return 0;
}

function getTemplateBuilderMode(template: ProposalTemplate): BuilderMode {
  return template.builder_mode === "drag_drop" ? "drag_drop" : "prebuilt";
}

function getTemplateFixedPagePolicy(template: ProposalTemplate): FixedPagePolicy {
  if (
    template.fixed_page_policy === "brand_defined" ||
    template.fixed_page_policy === "mixed"
  ) {
    return template.fixed_page_policy;
  }

  return "template_defined";
}

function isTemplateReady(template: ProposalTemplate): boolean {
  return (
    getTemplateServiceCount(template) > 0 &&
    getTemplatePageCount(template) > 0
  );
}

export function WizardStep1TemplateSelect({
  onSelect,
  selectedTemplateId,
}: Props) {
  const { data: templates = [], isLoading } = useProposalTemplates();
  const [selected, setSelected] = useState<string | null>(
    selectedTemplateId ?? null
  );

  useEffect(() => {
    setSelected(selectedTemplateId ?? null);
  }, [selectedTemplateId]);

  const handleSelect = (template: ProposalTemplate) => {
    setSelected(template.id);
    setTimeout(() => onSelect(template), 150);
  };

  const grouped = useMemo(
    () =>
      templates.reduce<Record<ClientType, ProposalTemplate[]>>(
        (acc, template) => {
          const key = getTemplateClientType(template);
          if (!acc[key]) acc[key] = [];
          acc[key].push(template);
          return acc;
        },
        {
          construction: [],
          restaurant: [],
          commercial: [],
          residential: [],
          other: [],
        }
      ),
    [templates]
  );

  const clientTypeOrder: ClientType[] = [
    "construction",
    "restaurant",
    "commercial",
    "residential",
    "other",
  ];

  const readyTemplates = templates.filter((template) => isTemplateReady(template)).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-5 w-5 animate-spin text-white/30" />
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/20">
              <Sparkles className="h-4 w-4 text-amber-400" />
            </div>

            <div>
              <p className="mb-1 text-sm font-semibold text-amber-300">
                You need a template first
              </p>
              <p className="text-sm leading-relaxed text-white/50">
                Proposals are now created from templates. Build a reusable
                template first, then use it as the starting point for customer
                proposals.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/proposal-builder/templates/new"
            className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-white/90"
          >
            <Plus className="h-4 w-4" />
            Create first template
          </Link>

          <Link
            to="/proposal-builder/templates"
            className="px-5 py-2.5 text-sm text-white/40 transition-colors hover:text-white/70"
          >
            View templates
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
        <p className="text-xs uppercase tracking-[0.14em] text-white/30">
          Template handoff
        </p>
        <h2 className="mt-2 text-lg font-semibold text-white">
          Choose the blueprint for this proposal
        </h2>
        <p className="mt-2 text-sm leading-6 text-white/45">
          The template becomes your proposal starting point. It brings in the
          default services, included pages, builder mode, AI context, and fixed-page
          direction that the rest of the wizard will build on.
        </p>

        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <StepSummaryCard
            label="Templates available"
            value={String(templates.length)}
          />
          <StepSummaryCard
            label="Ready to use"
            value={String(readyTemplates)}
          />
          <StepSummaryCard
            label="Grouped by client type"
            value={String(
              clientTypeOrder.filter((type) => grouped[type]?.length > 0).length
            )}
          />
        </div>
      </div>

      {clientTypeOrder
        .filter((type) => grouped[type]?.length > 0)
        .map((type) => {
          const cfg = CLIENT_TYPE_CONFIG[type];
          const typeTemplates = grouped[type];

          return (
            <div key={type}>
              <div className="mb-2 flex items-center gap-2 px-1">
                <span
                  className={cn(
                    "flex items-center gap-1.5 text-xs font-medium",
                    cfg.color
                  )}
                >
                  {cfg.icon}
                  {cfg.label}
                </span>
                <div className="h-px flex-1 bg-white/[0.06]" />
              </div>

              <div className="space-y-3">
                {typeTemplates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    isSelected={selected === template.id}
                    onSelect={handleSelect}
                    cfg={cfg}
                  />
                ))}
              </div>
            </div>
          );
        })}

      <div className="flex flex-wrap items-center gap-3 px-1">
        <Link
          to="/proposal-builder/templates"
          className="inline-flex items-center gap-1.5 text-xs text-white/25 transition-colors hover:text-white/50"
        >
          Manage templates
          <ChevronRight className="h-3.5 w-3.5" />
        </Link>

        <Link
          to="/proposal-builder/templates/new"
          className="inline-flex items-center gap-1.5 text-xs text-white/25 transition-colors hover:text-white/50"
        >
          Create a new template
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
}

function TemplateCard({
  template,
  isSelected,
  onSelect,
  cfg,
}: {
  template: ProposalTemplate;
  isSelected: boolean;
  onSelect: (t: ProposalTemplate) => void;
  cfg: (typeof CLIENT_TYPE_CONFIG)[ClientType];
}) {
  const serviceCount = getTemplateServiceCount(template);
  const pageCount = getTemplatePageCount(template);

  const builderMode = getTemplateBuilderMode(template);
  const builderCfg = BUILDER_MODE_CONFIG[builderMode];

  const fixedPagePolicy = getTemplateFixedPagePolicy(template);
  const fixedCfg = FIXED_PAGE_POLICY_CONFIG[fixedPagePolicy];

  const ready = isTemplateReady(template);

  return (
    <button
      type="button"
      onClick={() => onSelect(template)}
      className={cn(
        "flex w-full items-center gap-4 rounded-xl border px-5 py-4 text-left transition-all duration-150",
        isSelected
          ? cfg.selectedBg
          : "border-white/10 hover:border-white/20 hover:bg-white/[0.03]"
      )}
    >
      <div
        className={cn(
          "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-all",
          isSelected ? "border-white bg-white" : "border-white/20"
        )}
      >
        {isSelected && <div className="h-1.5 w-1.5 rounded-full bg-black" />}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-semibold text-white">
            {template.name}
          </span>

          {template.is_default && (
            <span className="flex shrink-0 items-center gap-0.5 text-[10px] font-medium text-amber-400">
              <Star className="h-2.5 w-2.5 fill-amber-400" />
              Default
            </span>
          )}

          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em]",
              builderCfg.color
            )}
          >
            {builderCfg.icon}
            {builderCfg.label}
          </span>

          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em]",
              fixedCfg.color
            )}
          >
            {fixedCfg.icon}
            {fixedCfg.label}
          </span>

          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em]",
              ready
                ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-300"
                : "border-white/10 bg-black/20 text-white/35"
            )}
          >
            <CheckCircle2 className="h-3 w-3" />
            {ready ? "Ready" : "Needs setup"}
          </span>
        </div>

        {template.description ? (
          <p className="mt-1 text-xs leading-5 text-white/40">
            {template.description}
          </p>
        ) : (
          <p className="mt-1 text-xs leading-5 text-white/28">
            No description added yet.
          </p>
        )}

        <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-white/35">
          <span className="rounded-full border border-white/10 bg-black/20 px-2.5 py-1">
            {serviceCount} service{serviceCount !== 1 ? "s" : ""}
          </span>
          <span className="rounded-full border border-white/10 bg-black/20 px-2.5 py-1">
            {pageCount} page{pageCount !== 1 ? "s" : ""}
          </span>
          {template.ai_tone ? (
            <span className="rounded-full border border-white/10 bg-black/20 px-2.5 py-1">
              {template.ai_tone}
            </span>
          ) : null}
        </div>

        <p className="mt-2 text-xs leading-5 text-white/28">
          {builderCfg.description} · {fixedCfg.description}
        </p>
      </div>

      <ChevronRight
        className={cn(
          "h-4 w-4 shrink-0 transition-colors",
          isSelected ? "text-white/60" : "text-white/20"
        )}
      />
    </button>
  );
}

function StepSummaryCard({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-4">
      <p className="text-[11px] uppercase tracking-[0.14em] text-white/30">
        {label}
      </p>
      <p className="mt-2 text-lg font-semibold text-white/85">{value}</p>
    </div>
  );
}